import { Button, Flex, Grid, Spinner, Text, useToast } from '@chakra-ui/react'
import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useBlocker } from 'react-router-dom'
import { AdminFeedbackModerationAction, AdminReportedFeedback } from '../../services/network'
import { createEventApi } from '../../utils/api-interface'
import { getErrorMessage } from '../../utils/utils'
import { ReportedFeedback } from '../../components/ReportedFeedback/ReportedFeedback'
import { DeleteConfirmationDialog } from '../../components/DeleteConfirmationDialog/DeleteConfirmationDialog'

interface IPendingFeedbackModeration {
  feedbackId: string
  action: AdminFeedbackModerationAction
}

function Reviews(): JSX.Element {
  const { t } = useTranslation()
  const toast = useToast()

  const [feedbacks, setFeedbacks] = useState<AdminReportedFeedback[]>([])
  const [loading, setLoading] = useState(true)
  const [loadingUpdate, setLoadingUpdate] = useState(false)
  const [pendingFeedbackModerations, setPendingFeedbackModerations] = useState<IPendingFeedbackModeration[]>([])

  const hasPendingFeedbackModerations = pendingFeedbackModerations.length >= 1
  const blocker = useBlocker(() => hasPendingFeedbackModerations)

  const fetchFeedbacks = useCallback(
    async (showLoading = true): Promise<void> => {
      if (showLoading) {
        setLoading(true)
      }
      try {
        const eventApi = createEventApi()
        const response = await eventApi.getReportedFeedbacksPendingModeration()
        setFeedbacks(response.data.feedbacks)
      } catch (error) {
        toast({
          title: t('feedback_fetch_error'),
          description: getErrorMessage(error),
          status: 'error',
          duration: 4000,
          isClosable: true
        })
      } finally {
        if (showLoading) {
          setLoading(false)
        }
      }
    },
    [t, toast]
  )

  useEffect(() => {
    fetchFeedbacks()
  }, [fetchFeedbacks])

  const alertUser = (e: { preventDefault: () => void; returnValue: string }): void => {
    e.preventDefault()
    e.returnValue = ''
  }

  useEffect(() => {
    if (hasPendingFeedbackModerations) {
      window.addEventListener('beforeunload', alertUser)
    } else {
      window.removeEventListener('beforeunload', alertUser)
    }

    return () => {
      window.removeEventListener('beforeunload', alertUser)
    }
  }, [hasPendingFeedbackModerations])

  const stageFeedbackModeration = useCallback((feedbackId: string, action: AdminFeedbackModerationAction): void => {
    setPendingFeedbackModerations((prevFeedbackModerations) => [
      ...prevFeedbackModerations.filter((feedbackModeration) => feedbackModeration.feedbackId !== feedbackId),
      { feedbackId, action }
    ])
  }, [])

  const cancelFeedbackModeration = useCallback((feedbackId: string): void => {
    setPendingFeedbackModerations((prevFeedbackModerations) =>
      prevFeedbackModerations.filter((feedbackModeration) => feedbackModeration.feedbackId !== feedbackId)
    )
  }, [])

  const getPendingFeedbackAction = useCallback(
    (feedbackId: string): AdminFeedbackModerationAction | undefined =>
      pendingFeedbackModerations.find((feedbackModeration) => feedbackModeration.feedbackId === feedbackId)?.action,
    [pendingFeedbackModerations]
  )

  const saveFeedbackModerations = useCallback(async (): Promise<void> => {
    if (loadingUpdate || !hasPendingFeedbackModerations) {
      return
    }

    setLoadingUpdate(true)
    try {
      const eventApi = createEventApi()

      for (const feedbackModeration of pendingFeedbackModerations) {
        await eventApi.moderateReportedFeedback(feedbackModeration.feedbackId, { action: feedbackModeration.action })
      }

      setPendingFeedbackModerations([])
      await fetchFeedbacks(false)
      toast({
        title: t('feedback_moderation_success'),
        status: 'success',
        duration: 4000,
        isClosable: true
      })
    } catch (error) {
      toast({
        title: t('feedback_moderation_error'),
        description: getErrorMessage(error),
        status: 'error',
        duration: 4000,
        isClosable: true
      })
    } finally {
      setLoadingUpdate(false)
    }
  }, [fetchFeedbacks, hasPendingFeedbackModerations, loadingUpdate, pendingFeedbackModerations, t, toast])

  const handleDialogConfirm = (): void => {
    setPendingFeedbackModerations([])
    if (blocker.state === 'blocked') {
      blocker.proceed()
    }
  }

  return (
    <Grid
      templateRows={'auto 1fr auto'}
      bg={'greyNeutral100'}
      flexDirection='column'
      padding={'40px 120px 20px 40px'}
      gap={'30px'}
      overflow={'auto'}
      height={'100vh'}>
      <Text fontSize='26px' fontWeight='700' lineHeight='31.2px' textAlign='left' marginTop={'30px'}>
        {t('reviews_moderation')}
      </Text>

      <Flex direction={'column'} gap={'12px'} overflow={'auto'}>
        {loading ? (
          <Flex justifyContent={'center'} alignItems={'center'} minHeight={'240px'}>
            <Spinner color='primary.400' size='lg' />
          </Flex>
        ) : feedbacks.length === 0 ? (
          <Text fontWeight='600' fontSize='16px' lineHeight='22.4px' color={'greyCold300'}>
            {t('empty_reported_feedbacks')}
          </Text>
        ) : (
          feedbacks.map((feedback) => (
            <ReportedFeedback
              key={feedback.feedbackId}
              feedback={feedback}
              isLoading={loadingUpdate}
              pendingAction={getPendingFeedbackAction(feedback.feedbackId)}
              onKeep={(): void => {
                stageFeedbackModeration(feedback.feedbackId, AdminFeedbackModerationAction.Keep)
              }}
              onRemove={(): void => {
                stageFeedbackModeration(feedback.feedbackId, AdminFeedbackModerationAction.Remove)
              }}
              onCancel={(): void => {
                cancelFeedbackModeration(feedback.feedbackId)
              }}
            />
          ))
        )}
      </Flex>
      <Flex direction={'row-reverse'} marginTop={'10px'}>
        {!loadingUpdate ? (
          <Button variant={'primary'} width={'211px'} onClick={saveFeedbackModerations} isDisabled={!hasPendingFeedbackModerations}>
            {t('save')}
          </Button>
        ) : hasPendingFeedbackModerations ? (
          <Text marginLeft={'2px'} fontWeight='600' fontSize='16px' lineHeight='22.4px' color={'greyCold300'}>
            {t('operation_in_progress', { operation: t('feedback_moderation_op') })}
          </Text>
        ) : (
          <></>
        )}
      </Flex>
      {blocker.state === 'blocked' && (
        <DeleteConfirmationDialog
          isOpen={blocker.state === 'blocked'}
          onClose={(): void => blocker.reset()}
          onConfirm={(): void => handleDialogConfirm()}
          type='feedback_blocker'
        />
      )}
    </Grid>
  )
}

export { Reviews }
